import { useSidebar } from "@/components/ui/sidebar";
import * as React from "react";

// ---------------------------------------------------------------------------
// SVG geometry
// ---------------------------------------------------------------------------
const FULL_W = 293.07;
const FULL_H = 70.67;
const COLLAPSED_W = 72.69;
const B_SHIFT = -124.54;

// ---------------------------------------------------------------------------
// Animation timing (matches sidebar's duration-200 ease-linear)
// ---------------------------------------------------------------------------
const SIDEBAR_DURATION = 200;
const SIDEBAR_EASING = "linear";
const IGNITE_STAGGER = 100;
const NEON_COLOR = "#4488ff";

// Flicker sequence per letter: [time_offset_ms, opacity, glow_intensity]
const FLICKER_SEQUENCE: ReadonlyArray<readonly [number, number, number]> = [
  [0, 0.8, 1.0],
  [40, 0.1, 0.0],
  [80, 0.6, 0.7],
  [110, 0.05, 0.0],
  [160, 0.9, 1.2],
  [190, 0.3, 0.3],
  [230, 1.0, 1.0],
  [350, 1.0, 0.0],
];

// ---------------------------------------------------------------------------
// Letter definitions
// ---------------------------------------------------------------------------
type LetterDef = {
  readonly id: string;
  readonly d: string;
};

const LETTERS: ReadonlyArray<LetterDef> = [
  {
    id: "N1",
    d: "M13.33,69.48H0V1.1h13.42l7.08,27.94,1.47,8.09h.18l-.55-9.83V1.1h13.23v68.37h-13.42l-7.08-27.66-1.38-8.09h-.18l.55,9.83v25.92Z",
  },
  {
    id: "E1",
    d: "M66.81,69.48h-27.11V1.1h26.65v11.95h-13.05v15.81h11.49v11.4h-11.49v17.09h13.51v12.13Z",
  },
  {
    id: "O",
    d: "M74.3,65.85c-2.85-3.22-4.27-7.95-4.27-14.2V18.93c0-6.19,1.42-10.89,4.27-14.11,2.85-3.22,7-4.82,12.45-4.82s9.62,1.61,12.5,4.82c2.88,3.22,4.32,7.92,4.32,14.11v32.72c0,6.25-1.42,10.98-4.27,14.2-2.85,3.22-7.03,4.82-12.54,4.82s-9.6-1.61-12.45-4.82ZM89.97,53.76V16.82c0-3.19-1.07-4.78-3.22-4.78s-3.12,1.59-3.12,4.78v36.94c0,3.25,1.04,4.87,3.12,4.87s3.22-1.62,3.22-4.87Z",
  },
  {
    id: "N2",
    d: "M121.12,69.48h-13.33V1.1h13.42l7.08,27.94,1.47,8.09h.18l-.55-9.83V1.1h13.23v68.37h-13.42l-7.08-27.66-1.38-8.09h-.18l.55,9.83v25.92Z",
  },
  {
    id: "B",
    d: "M164.59,69.48V1.1h15.35c4.84,0,8.61,1.09,11.3,3.26,2.7,2.18,4.04,5.22,4.04,9.14v8.91c0,5.09-2.08,8.55-6.25,10.38v.09c2.63.55,4.61,1.78,5.93,3.68,1.32,1.9,1.98,4.26,1.98,7.08v11.67c0,4.41-1.32,7.87-3.95,10.39-2.63,2.51-6.56,3.77-11.76,3.77h-16.64ZM179.57,28.76c1.29,0,2.22-.35,2.8-1.06.58-.7.87-1.79.87-3.26v-8.64c0-1.35-.28-2.31-.83-2.89-.55-.58-1.5-.87-2.85-.87h-1.75v16.73h1.75ZM179.66,58.63c1.41,0,2.42-.34,3.03-1.01.61-.67.92-1.78.92-3.31v-10.84c0-1.53-.32-2.62-.97-3.26-.64-.64-1.67-.97-3.08-.97h-1.75v19.39h1.84Z",
  },
  { id: "L", d: "M226.16,69.48h-25.46V1.1h13.6v55.88h11.85v12.5Z" },
  {
    id: "U",
    d: "M232.32,66.26c-2.7-2.94-4.04-7.54-4.04-13.79V1.1h13.51v52.75c0,3.19,1.04,4.78,3.12,4.78.98,0,1.73-.41,2.25-1.24.52-.83.78-2.01.78-3.54V1.1h13.6v51.28c0,6.31-1.35,10.94-4.04,13.88s-6.89,4.41-12.59,4.41-9.9-1.47-12.59-4.41Z",
  },
  {
    id: "E2",
    d: "M293.07,69.48h-27.11V1.1h26.65v11.95h-13.05v15.81h11.49v11.4h-11.49v17.09h13.51v12.13Z",
  },
];

const KEEP = new Set(["N1", "B"]);
const IGNITE_ORDER = ["E1", "O", "N2", "L", "U", "E2"] as const;

// ---------------------------------------------------------------------------
// Letter visual state
// ---------------------------------------------------------------------------
type LetterState = { opacity: number; glow: number; };
type LetterStates = Record<string, LetterState>;

const INITIAL_VISIBLE: LetterStates = Object.fromEntries(
  IGNITE_ORDER.map((id) => [id, { opacity: 1, glow: 0 }]),
);
const INITIAL_HIDDEN: LetterStates = Object.fromEntries(
  IGNITE_ORDER.map((id) => [id, { opacity: 0, glow: 0 }]),
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const NeonBlueLogo: React.FC = () => {
  const { state } = useSidebar();
  const expanded = state === "expanded";

  const filterId = React.useId();
  const logoId = React.useId();

  const [letterStates, setLetterStates] = React.useState<LetterStates>(
    () => (expanded ? INITIAL_VISIBLE : INITIAL_HIDDEN),
  );
  const [bAtFull, setBAtFull] = React.useState(expanded);

  const prevExpanded = React.useRef(expanded);
  const isInitial = React.useRef(true);
  const timers = React.useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const clearTimers = React.useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const schedule = React.useCallback((fn: () => void, ms: number) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
    return id;
  }, []);

  React.useEffect(() => {
    if (isInitial.current) {
      isInitial.current = false;
      prevExpanded.current = expanded;
      return;
    }
    if (expanded === prevExpanded.current) return;
    prevExpanded.current = expanded;
    clearTimers();

    if (!expanded) {
      // === COLLAPSE: instant hide + B slides left ===
      setLetterStates(INITIAL_HIDDEN);
      setBAtFull(false);
    } else {
      // === EXPAND: B slides right, then neon ignition ===
      setBAtFull(true);

      schedule(() => {
        for (const [letterIdx, letterId] of IGNITE_ORDER.entries()) {
          const letterDelay = letterIdx * IGNITE_STAGGER;

          for (const [timeOffset, opacity, glow] of FLICKER_SEQUENCE) {
            schedule(() => {
              setLetterStates((prev) => ({
                ...prev,
                [letterId]: { opacity, glow },
              }));
            }, letterDelay + timeOffset);
          }
        }
      }, SIDEBAR_DURATION + 30);
    }

    return clearTimers;
  }, [expanded, clearTimers, schedule]);

  // Compute widths from height=24px
  const svgW = (FULL_W / FULL_H) * 24;
  const nbW = (COLLAPSED_W / FULL_H) * 24;
  const containerW = bAtFull ? svgW : nbW;

  // Fixed left margin so NB is centered in collapsed sidebar.
  // Sidebar = 3.25rem (52px), SidebarHeader p-2 (8px each side) → 36px content area.
  // margin = (36 - nbW) / 2
  const marginLeft = (36 - nbW) / 2;

  return (
    <div
      className="h-9 shrink-0 overflow-hidden flex items-center"
      style={{
        width: containerW,
        marginLeft,
        transition: `width ${SIDEBAR_DURATION}ms ${SIDEBAR_EASING}`,
      }}
    >
      <svg
        viewBox={`0 0 ${FULL_W} ${FULL_H}`}
        preserveAspectRatio="xMinYMid meet"
        className="h-6 shrink-0"
        style={{ width: svgW, overflow: "visible" }}
        role="img"
        aria-labelledby={logoId}
      >
        <title id={logoId}>Neon Blue logo</title>
        <defs>
          <filter
            id={filterId}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="4"
              result="blur"
            />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="0.3 0 0 0 0.1  0 0.5 0 0 0.3  0 0 1 0 0.9  0 0 0 1 0"
              result="blue-blur"
            />
            <feMerge>
              <feMergeNode in="blue-blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Glow layer — behind letters that are igniting */}
        {IGNITE_ORDER.map((id) => {
          const letter = LETTERS.find((l) => l.id === id);
          const ls = letterStates[id];
          if (!letter || !ls || ls.glow <= 0) return null;

          return (
            <path
              key={`glow-${id}`}
              d={letter.d}
              fill={NEON_COLOR}
              filter={`url(#${filterId})`}
              style={{ opacity: ls.glow * 0.6, pointerEvents: "none" }}
            />
          );
        })}

        {/* Main letter paths */}
        {LETTERS.map((letter) => {
          const isB = letter.id === "B";
          const isN = letter.id === "N1";
          const disappears = !KEEP.has(letter.id);
          const ls = letterStates[letter.id];

          let style: React.CSSProperties;

          if (isB) {
            style = {
              transform: bAtFull
                ? "translateX(0)"
                : `translateX(${B_SHIFT}px)`,
              transition: `transform ${SIDEBAR_DURATION}ms ${SIDEBAR_EASING}`,
              opacity: 1,
            };
          } else if (isN) {
            style = { opacity: 1 };
          } else if (disappears && ls) {
            const glowAmount = ls.glow || 0;
            style = {
              opacity: ls.opacity,
              fill: glowAmount > 0
                ? `color-mix(in srgb, currentColor, ${NEON_COLOR} ${Math.round(glowAmount * 30)}%)`
                : undefined,
            };
          } else {
            style = {};
          }

          return (
            <path
              key={letter.id}
              d={letter.d}
              fill="currentColor"
              style={style}
            />
          );
        })}
      </svg>
    </div>
  );
};
